import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Crown, Loader2, UserCog, Check } from 'lucide-react';
import { LemitSocio } from '../../../../types';

// Modal para definir QUEM é o representante legal quando a empresa tem 2+ sócios.
// O sistema não adivinha: sugere o de maior participação e o usuário confirma.

const fmtCpf = (doc?: string): string => {
    const c = (doc || '').replace(/\D/g, '');
    return c.length === 11 ? c.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : (doc || '—');
};

interface Props {
    isOpen: boolean;
    onClose: () => void;
    socios: LemitSocio[];
    empresaNome?: string;
    /** CPF já definido (para pré-selecionar ao trocar). */
    cpfAtual?: string | null;
    salvando?: boolean;
    onConfirm: (socio: LemitSocio) => void;
}

const RepresentanteModal: React.FC<Props> = ({ isOpen, onClose, socios, empresaNome, cpfAtual, salvando, onConfirm }) => {
    const ordenados = socios.slice().sort((a, b) => (b.participacao ?? 0) - (a.participacao ?? 0));
    const sugerido = ordenados[0];
    const [escolhido, setEscolhido] = useState<string>(
        (cpfAtual || '').replace(/\D/g, '') || (sugerido?.cpf || '').replace(/\D/g, ''),
    );

    if (!isOpen) return null;
    const socioEscolhido = ordenados.find(s => (s.cpf || '').replace(/\D/g, '') === escolhido);

    return createPortal(
        <div className="fixed inset-0 z-[2100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-2 bg-white/20 rounded-xl"><UserCog size={18} /></div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-extrabold leading-tight">Quem é o representante legal?</h2>
                            <p className="text-[11px] text-white/85 truncate">{empresaNome || 'Empresa com mais de um sócio'}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/20"><X size={18} /></button>
                </div>

                <div className="p-4 space-y-2 max-h-[55vh] overflow-y-auto">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                        Esta empresa tem <b>{socios.length} sócios</b>. Escolha quem assina — o CPF dele vai para o cadastro e é usado no termo.
                    </p>
                    {ordenados.map((s, i) => {
                        const cpf = (s.cpf || '').replace(/\D/g, '');
                        const ativo = cpf === escolhido;
                        const isSugerido = i === 0;
                        return (
                            <button
                                key={cpf || i}
                                onClick={() => setEscolhido(cpf)}
                                disabled={cpf.length !== 11}
                                className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all disabled:opacity-40 ${ativo
                                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-300'
                                    : 'border-slate-200 dark:border-slate-700 hover:border-amber-300 bg-slate-50/60 dark:bg-slate-800/40'}`}
                            >
                                <div className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${ativo ? 'border-amber-500 bg-amber-500' : 'border-slate-300 dark:border-slate-600'}`}>
                                    {ativo && <Check size={10} className="text-white" strokeWidth={4} />}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate flex items-center gap-1.5">
                                        {isSugerido && <Crown size={12} className="text-amber-500 shrink-0" />}{s.nome || 'Sócio sem nome'}
                                    </p>
                                    <p className="text-[11px] font-mono text-slate-500">{fmtCpf(s.cpf)}{cpf.length !== 11 ? ' · CPF indisponível' : ''}</p>
                                </div>
                                <span className="text-xs font-extrabold text-primary shrink-0">
                                    {s.participacao != null ? `${s.participacao}%` : '—'}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
                    <button onClick={onClose} className="px-4 py-2 text-xs font-bold rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                        Cancelar
                    </button>
                    <button
                        onClick={() => socioEscolhido && onConfirm(socioEscolhido)}
                        disabled={!socioEscolhido || !!salvando}
                        className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40"
                    >
                        {salvando ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Definir representante
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default RepresentanteModal;
